import React from 'react'
import Header from './Header'
import Footer from './Footer'
import { useNavigate } from 'react-router-dom'

function About() {
    const navigate = useNavigate();
  return (
    <div className="bg-white overflow-x-hidden flex flex-col">
      <Header />
      
      <div className="flex-1 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">About Coded Estate</h1>
            
            <div className="space-y-6 text-gray-700">
              <section>
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Built on-chain. Designed to decentralize. Focused on ownership.
                  </h2>
                </div>
                
                <p className="mb-6 text-lg leading-relaxed">
                  Founded to challenge the conventions of legacy real estate systems, Coded Estate is a fully integrated, 
                  blockchain-native platform that delivers a seamless, decentralized experience for listing, booking, 
                  investing, and transacting in real-world real estate assets (RWAs). Coded Estate merges artificial 
                  intelligence, blockchain technology, and smart contracts into a unified user experience eliminating 
                  traditional intermediaries.
                </p>
                
                <p className="mb-8 text-lg leading-relaxed">
                  Whether you are a short-term traveler, a long-term investor, or an everyday home buyer, Coded Estate 
                  offers a modern, fully transparent ecosystem to discover, acquire, trade, rent, or fractionalize real 
                  estate in ways previously constrained by outdated systems, excessive fees, and fragmented intermediaries.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">The Vision</h2>
                
                <div className="bg-[#5B1DEE] bg-opacity-5 rounded-lg p-6 mb-6">
                  <p className="text-lg font-semibold text-gray-900 mb-4">
                    At the heart of Coded Estate lies a singular belief: that real estate should be borderless, 
                    accessible, automated, and user-owned.
                  </p>
                </div>
                
                <p className="mb-6 text-lg leading-relaxed">
                  Coded Estate aims not simply to digitize real estate processes, but to reinvent them—transforming 
                  how people around the world engage with property, from booking high-end residences for a weekend stay, 
                  to fractionalizing ownership of multi-unit buildings. It does so by embedding smart contracts, 
                  verifiable credentials, dynamic pricing engines, and predictive AI assistants into each touchpoint 
                  of the platform.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Platform Features</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Blockchain Integration</h3>
                    <p className="text-gray-700">
                      Built on cutting-edge blockchain technology to ensure transparency, security, and decentralized ownership.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Smart Contracts</h3>
                    <p className="text-gray-700">
                      Automated contracts that eliminate intermediaries and reduce transaction costs while ensuring trust.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">AI-Powered Insights</h3>
                    <p className="text-gray-700">
                      Advanced artificial intelligence provides market insights, pricing recommendations, and personalized experiences.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Fractional Ownership</h3>
                    <p className="text-gray-700">
                      Enable multiple investors to own fractions of high-value properties, democratizing real estate investment.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Coded Estate?</h2>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-[#5B1DEE] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Transparent Transactions</h3>
                      <p className="text-gray-700">All transactions are recorded on the blockchain, providing complete transparency and immutable records.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-[#5B1DEE] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Reduced Fees</h3>
                      <p className="text-gray-700">By eliminating traditional intermediaries, we significantly reduce transaction fees and processing costs.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-[#5B1DEE] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Global Accessibility</h3>
                      <p className="text-gray-700">Access real estate opportunities worldwide without geographical limitations or traditional banking restrictions.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-[#5B1DEE] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">User Ownership</h3>
                      <p className="text-gray-700">True ownership of your assets and data, with complete control over your real estate portfolio.</p>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Join the Future of Real Estate</h2>
                <div className="bg-gradient-to-r from-[#5B1DEE] to-purple-700 rounded-lg p-8 text-white text-center">
                  <h3 className="text-2xl font-bold mb-4">Ready to Experience Decentralized Real Estate?</h3>
                  <p className="text-lg mb-6 opacity-90">
                    Join thousands of users who are already transforming their real estate experience with Coded Estate.
                  </p>
                  <button className="bg-white text-[#5B1DEE] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                  onClick={()=> navigate("/onboarding/steps")}
                  >
                    Get Started Today
                  </button>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default About
